import React, { useCallback, useState, useEffect } from 'react';
import { getUserRoleTitle, storage } from 'utils';
import { useAppDispatch, useAppSelector, useAuth0 } from 'hooks';
import everbeatLogo from 'assets/everbeat-logo.svg';
import styles from './styles.module.sass';
import { LoginLogoutButton } from './components';
import { notificationActions } from 'utils/store/slice/notification';
import { NotificationType, RoutePath, StorageKey, UserRole } from 'types/enums';
import { Link, useNavigate } from 'react-router-dom';
import { LoginSelectionModal, PartnerLoginModal } from 'components';
import { userActions } from 'utils/store';
import { config } from 'packages/config';

export const Header = () => {
  const { logout, loginWithRedirect, isAuthenticated, user } = useAuth0();
  const userData = useAppSelector(({ user }) => user.data);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showLoginSelection, setShowLoginSelection] = useState(false);
  const [showPartnerLogin, setShowPartnerLogin] = useState(false);
  const [isPartnerLoginLoading, setIsPartnerLoginLoading] = useState(false);
  const [isPartnerLoggedIn, setIsPartnerLoggedIn] = useState(false);

  useEffect(() => {
    const checkPartnerLoginStatus = async () => {
      const isPartnerLogin = await storage.get(StorageKey.IS_PARTNER_LOGIN);
      setIsPartnerLoggedIn(isPartnerLogin === 'true');
    };

    checkPartnerLoginStatus();
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      const isPartnerLogin = await storage.get(StorageKey.IS_PARTNER_LOGIN);

      if (isPartnerLogin === 'true') {
        await storage.drop(StorageKey.PARTNER_TOKEN);
        await storage.drop(StorageKey.IS_PARTNER_LOGIN);
        await storage.drop(StorageKey.TOKEN);
        await storage.drop(StorageKey.PARTNER_DATA);
        dispatch(userActions.clearUser());
        setIsPartnerLoggedIn(false);
        window.location.href = window.location.origin; // Use href instead of reload
      } else {
        // Clear any local data first
        dispatch(userActions.clearUser());
        await storage.drop(StorageKey.TOKEN);

        // Then call Auth0 logout
        await logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      }
    } catch (error) {
      dispatch(notificationActions.notify({ message: 'Logout failed', type: NotificationType.ERROR }));
      console.error('Logout error:', error);
    }
  }, [dispatch, logout]);

  const handleLogin = useCallback(() => {
    setShowLoginSelection(true);
  }, []);

  const handleAuth = useCallback(async () => {
    if (isAuthenticated || isPartnerLoggedIn) {
      return await handleLogout();
    }

    return handleLogin();
  }, [handleLogin, handleLogout, isAuthenticated, isPartnerLoggedIn]);

  const handleEverbeatLogin = useCallback(async () => {
    try {
      await loginWithRedirect();
    } catch (error) {
      dispatch(notificationActions.notify({ message: 'Login failed', type: NotificationType.ERROR }));
      console.error(error);
    }
  }, [dispatch, loginWithRedirect]);

  const handlePartnerLoginSelect = useCallback(() => {
    setShowPartnerLogin(true);
  }, []);

  const handlePartnerLogin = useCallback(
    async (partnerId: string, token: string) => {
      setIsPartnerLoginLoading(true);
      try {
        // First, validate the partner credentials and get partner details
        console.log(`Calling partner validate API: /partner/validate/${partnerId}/${token}`);

        const validateResponse = await fetch(
          `${config.ENV.API.EVERBEAT_BASE_URL}/partner/validate/${partnerId}/${token}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );

        console.log('Partner validate response status:', validateResponse.status);

        if (!validateResponse.ok) {
          const errorText = await validateResponse.text();
          console.log('Partner validate error response:', errorText);
          throw new Error(`Invalid partner credentials: ${validateResponse.status}`);
        }

        const partnerData = await validateResponse.json();

        // Debug: Log the actual response to understand the structure
        console.log('Partner validation response:', partnerData);

        // Check if partner is active (only if status field exists)
        if (partnerData.status && partnerData.status !== 'active') {
          throw new Error(`Partner account is ${partnerData.status}. Please contact support.`);
        }

        await storage.set(StorageKey.PARTNER_TOKEN, token);
        await storage.set(StorageKey.IS_PARTNER_LOGIN, 'true');
        await storage.set(StorageKey.TOKEN, token);

        // Validate that we have the minimum required partner data
        if (!partnerData || !partnerData.name) {
          throw new Error('Invalid partner data received from server');
        }

        // Use the actual partner data from the API response
        const partnerUser = {
          id: partnerData.id || partnerId, // Use the actual partner UUID from API or fallback
          email: partnerData.contactEmail || `partner-${partnerId}@partner.everbeat.com`,
          firstName: partnerData.name || partnerId, // Company name for display
          lastName: '',
          username: partnerId, // Keep the partner ID for API calls
          name: partnerData.name, // Company name from API
          role: UserRole.PARTNER,
          phoneNumber: partnerData.contactPhone || '',
          createdAt: partnerData.created_at || new Date().toISOString(),
          updatedAt: partnerData.updated_at || new Date().toISOString(),
          // Additional partner-specific fields
          partnerId: partnerId, // Store the original partner ID
          partnerStatus: partnerData.status || 'unknown',
          partnerType: partnerData.type || 'unknown',
          apiKey: partnerData.apiKey,
        };

        await storage.set(StorageKey.PARTNER_DATA, JSON.stringify(partnerUser));
        dispatch(userActions.setUser(partnerUser));
        dispatch(userActions.setToken(token));
        setIsPartnerLoggedIn(true);
        setShowPartnerLogin(false);

        dispatch(
          notificationActions.notify({
            message: `Successfully logged in as ${partnerData.name || 'partner'}`,
            type: NotificationType.SUCCESS,
          }),
        );

        // Navigate to root and let it handle the appropriate redirect
        navigate('/');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Partner login failed';
        dispatch(
          notificationActions.notify({
            message: errorMessage,
            type: NotificationType.ERROR,
          }),
        );
        console.error('Partner login error:', error);
      } finally {
        setIsPartnerLoginLoading(false);
      }
    },
    [dispatch],
  );

  const isLoggedIn = isAuthenticated || isPartnerLoggedIn;

  return (
    <>
      <div className={styles.container}>
        <Link to={RoutePath.PRACTICE}>
          <img height={70} src={everbeatLogo} draggable={false} alt='logo' />
        </Link>
        {isLoggedIn && (
          <>
            <div className={styles.name_wrapper}>
              <span>
                {userData?.role === UserRole.PARTNER
                  ? (userData as any).name || userData.firstName || 'Partner Company'
                  : userData
                  ? `${userData.firstName} ${userData.lastName}`
                  : user?.name}
              </span>
              {userData && <span> {getUserRoleTitle(userData.role)}</span>}
            </div>
          </>
        )}
        <LoginLogoutButton isLogin={!isLoggedIn} onClick={handleAuth} />
      </div>

      <LoginSelectionModal
        open={showLoginSelection}
        onClose={() => setShowLoginSelection(false)}
        onSelectEverbeat={handleEverbeatLogin}
        onSelectPartner={handlePartnerLoginSelect}
      />

      <PartnerLoginModal
        open={showPartnerLogin}
        onClose={() => setShowPartnerLogin(false)}
        onSubmit={handlePartnerLogin}
        isLoading={isPartnerLoginLoading}
      />
    </>
  );
};
